import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  mapBackendConditionsToFrontend,
  mapFrontendConditionsToBackend,
} from 'utils/wantListDefaultsMapper';
import {
  mapBackendToFrontend as mapBackendLanguagesToFrontend,
  mapFrontendToBackend as mapFrontendLanguagesToBackend,
} from 'constants/languages';
import { CardCondition } from 'types/interfaces/condition/condition';
import { HaveFinish } from 'utils/finishes';

/**
 * Backend response format for want list defaults
 */
interface WantListDefaultsBackend {
  defaultAcceptFoil: boolean;
  defaultAcceptNonFoil: boolean;
  /** Track D; absent on a pre-D backend (then follows defaultAcceptFoil, the migration rule) */
  defaultAcceptEtched?: boolean;
  defaultLanguages: string[]; // lowercase: ['en', 'es', 'fr', ...]
  defaultConditions: string[]; // uppercase: ['NEAR_MINT', 'SLIGHTLY_PLAYED', ...]
}

/**
 * Frontend format for want list defaults (used by UI components)
 */
export interface WantListDefaults {
  defaultAcceptFoil: boolean;
  defaultAcceptNonFoil: boolean;
  defaultAcceptEtched: boolean;
  defaultLanguages: string[]; // uppercase: ['EN', 'ES', 'FR', ...]
  defaultConditions: string[]; // codes: ['NM', 'LP', 'MP', ...]
}

/**
 * Convert backend response to frontend format
 */
function transformBackendToFrontend(backend: WantListDefaultsBackend): WantListDefaults {
  return {
    defaultAcceptFoil: backend.defaultAcceptFoil,
    defaultAcceptNonFoil: backend.defaultAcceptNonFoil,
    defaultAcceptEtched: backend.defaultAcceptEtched ?? backend.defaultAcceptFoil,
    defaultLanguages: mapBackendLanguagesToFrontend(backend.defaultLanguages),
    defaultConditions: mapBackendConditionsToFrontend(backend.defaultConditions),
  };
}

/**
 * Convert frontend format to backend request format
 */
function transformFrontendToBackend(frontend: Partial<WantListDefaults>): Partial<WantListDefaultsBackend> {
  const backend: Partial<WantListDefaultsBackend> = {};

  if (frontend.defaultAcceptFoil !== undefined) {
    backend.defaultAcceptFoil = frontend.defaultAcceptFoil;
  }
  if (frontend.defaultAcceptNonFoil !== undefined) {
    backend.defaultAcceptNonFoil = frontend.defaultAcceptNonFoil;
  }
  if (frontend.defaultAcceptEtched !== undefined) {
    backend.defaultAcceptEtched = frontend.defaultAcceptEtched;
  }
  if (frontend.defaultLanguages !== undefined) {
    backend.defaultLanguages = mapFrontendLanguagesToBackend(frontend.defaultLanguages);
  }
  if (frontend.defaultConditions !== undefined) {
    backend.defaultConditions = mapFrontendConditionsToBackend(frontend.defaultConditions);
  }

  return backend;
}

/**
 * Have list defaults (used for received cards behavior)
 */
export interface HaveListDefaults {
  receivedCardsForTrade: boolean;
  /**
   * Opt-out flag, default true. When false, settlement does not modify the
   * receiver's Have List, and the server atomically forces
   * receivedCardsForTrade to false in the same write.
   */
  addReceivedCardsToHaveList?: boolean;
  defaultHaveListAlert?: boolean;
  alertsSentThisMonth?: number;
  alertsMonthlyLimit?: number;
  totalCards?: number;
  alertEnabledCount?: number;
  /**
   * Entry defaults: what a card is added as from the Have List toolbar. Absent
   * on a backend that predates them (then the stock NM / non-foil / English).
   */
  defaultEntryCondition?: CardCondition;
  defaultEntryFinish?: HaveFinish;
  /** Card-table language code: en, ja, ... zhs, zht (not zh-hans / zh-hant). */
  defaultEntryLanguage?: string;
}

/**
 * PUT /settings/have-list-defaults responds with a narrow 3-boolean subset,
 * never the computed alert fields — and its defaultHaveListAlert is forced
 * false for accounts without alert access regardless of the stored value.
 * Merge fields into the cached GET payload; never replace it with this object.
 */
export interface UpdateHaveListDefaultsResponse {
  success: boolean;
  settings: {
    receivedCardsForTrade: boolean;
    addReceivedCardsToHaveList: boolean;
    defaultHaveListAlert: boolean;
    defaultEntryCondition?: CardCondition;
    defaultEntryFinish?: HaveFinish;
    defaultEntryLanguage?: string;
  };
}

/**
 * Feed preferences (community feed privacy toggle)
 */
export interface FeedPreferences {
  showInFeed: boolean;
}

/**
 * Privacy preferences (social follow graph + newsfeed visibility + offer reachability)
 */
export interface PrivacyPreferences {
  allowFollowers: boolean;
  showWantActivityToFollowers: boolean;
  showTradeActivityToFollowers: boolean;
  followingFeedEnabled: boolean;
  /**
   * Opt-OUT reachability flag, default true. When false the user is removed from
   * offer discovery (search holder counts, the counterparty picker, suggestions)
   * AND cannot create offers themselves — the reciprocity rule that stops the
   * setting becoming a one-way exemption. Enforcement is server-side and
   * authoritative; the UI only saves the user a round trip.
   */
  allowIncomingTradeOffers: boolean;
}

export interface MaintenanceStatus {
  maintenanceMode: boolean;
  updatedAt: string;
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: baseQuery,
  tagTypes: ['WantListDefaults', 'HaveListDefaults', 'FeedPreferences', 'PrivacyPreferences'],
  endpoints: (builder) => ({
    getWantListDefaults: builder.query<WantListDefaults, void>({
      query: () => ({
        url: '/settings/want-list-defaults',
        method: 'GET',
        requiresAuth: true,
      }),
      transformResponse: (response: WantListDefaultsBackend) => {
        return transformBackendToFrontend(response);
      },
      providesTags: ['WantListDefaults'],
    }),

    updateWantListDefaults: builder.mutation<
      { success: boolean; settings: WantListDefaults },
      Partial<WantListDefaults>
    >({
      query: (body) => ({
        url: '/settings/want-list-defaults',
        method: 'PUT',
        body: transformFrontendToBackend(body),
        requiresAuth: true,
      }),
      transformResponse: (response: { success: boolean; settings: WantListDefaultsBackend }) => {
        return {
          success: response.success,
          settings: transformBackendToFrontend(response.settings),
        };
      },
      // Optimistic so every reader (Settings, the Want List "Accepting" pill)
      // shows the write at once; the refetch below then confirms it.
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData('getWantListDefaults', undefined, (draft) => {
            Object.assign(draft, arg);
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: ['WantListDefaults'],
    }),

    getHaveListDefaults: builder.query<HaveListDefaults, void>({
      query: () => ({
        url: '/settings/have-list-defaults',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['HaveListDefaults'],
    }),

    updateHaveListDefaults: builder.mutation<
      UpdateHaveListDefaultsResponse,
      Partial<HaveListDefaults>
    >({
      query: (body) => ({
        url: '/settings/have-list-defaults',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistically apply the write, including the server's coupling rule:
        // opting out of adding received cards also turns the for-trade default off.
        const patchResult = dispatch(
          settingsApi.util.updateQueryData('getHaveListDefaults', undefined, (draft) => {
            Object.assign(draft, arg);
            if (arg.addReceivedCardsToHaveList === false) {
              draft.receivedCardsForTrade = false;
            }
          })
        );
        try {
          const { data } = await queryFulfilled;
          // Reconcile the coupled booleans from the response. defaultHaveListAlert
          // is excluded: the PUT response forces it false for accounts without
          // alert access, so the alert toggle's refetch owns that field instead.
          dispatch(
            settingsApi.util.updateQueryData('getHaveListDefaults', undefined, (draft) => {
              draft.receivedCardsForTrade = data.settings.receivedCardsForTrade;
              draft.addReceivedCardsToHaveList = data.settings.addReceivedCardsToHaveList;
              if (data.settings.defaultEntryCondition !== undefined) {
                draft.defaultEntryCondition = data.settings.defaultEntryCondition;
              }
              if (data.settings.defaultEntryFinish !== undefined) {
                draft.defaultEntryFinish = data.settings.defaultEntryFinish;
              }
              if (data.settings.defaultEntryLanguage !== undefined) {
                draft.defaultEntryLanguage = data.settings.defaultEntryLanguage;
              }
            })
          );
        } catch {
          patchResult.undo();
        }
      },
      // Alert writes still refetch so the computed quota fields stay fresh; the
      // received-cards toggles render from the response with no second request.
      invalidatesTags: (result, error, arg) =>
        'defaultHaveListAlert' in arg ? ['HaveListDefaults'] : [],
    }),

    getFeedPreferences: builder.query<FeedPreferences, void>({
      query: () => ({
        url: '/settings/feed-preferences',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['FeedPreferences'],
    }),

    updateFeedPreferences: builder.mutation<
      { success: boolean; settings: FeedPreferences },
      Partial<FeedPreferences>
    >({
      query: (body) => ({
        url: '/settings/feed-preferences',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['FeedPreferences'],
    }),

    getPrivacyPreferences: builder.query<PrivacyPreferences, void>({
      query: () => ({
        url: '/settings/privacy-preferences',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['PrivacyPreferences'],
    }),

    updatePrivacyPreferences: builder.mutation<
      { success: boolean; settings: PrivacyPreferences },
      Partial<PrivacyPreferences>
    >({
      query: (body) => ({
        url: '/settings/privacy-preferences',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['PrivacyPreferences'],
    }),

    getMaintenanceStatus: builder.query<MaintenanceStatus, void>({
      query: () => ({
        url: '/settings/maintenance-status',
        method: 'GET',
        requiresAuth: false,
      }),
    }),
  }),
});

export const {
  useGetWantListDefaultsQuery,
  useUpdateWantListDefaultsMutation,
  useGetHaveListDefaultsQuery,
  useUpdateHaveListDefaultsMutation,
  useGetFeedPreferencesQuery,
  useUpdateFeedPreferencesMutation,
  useGetPrivacyPreferencesQuery,
  useUpdatePrivacyPreferencesMutation,
  useGetMaintenanceStatusQuery,
} = settingsApi;
